import { makeStyles } from '@material-ui/styles'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { humanReadableSize } from 'app/plugins/openstack/helpers'
import Progress from 'core/components/progress/Progress'
import SimpleLink from 'core/components/SimpleLink'
import Card from 'core/elements/card'
import Grid from 'core/elements/grid'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'
import { resmgrHostsByIdSelector } from 'openstack/components/infrastructure/selectors'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import OverviewCard from 'openstack/components/vms/vm-details/OverviewCard'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isNilOrEmpty } from 'utils/fp'
import { formatDate } from 'utils/misc'
import { StatusCellComponent as SnapshotStatusCellComponent } from '../../snapshots/VolumeSnapshotsListPage'
import { StatusCellComponent } from '../VolumesListPage'
import { VolumeSourceCell } from './VolumeSourceCell'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '370px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
}))

const volumeSnapshotTableColumns = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'id',
    label: 'UUID',
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'status',
    label: 'Status',
    CellComponent: SnapshotStatusCellComponent,
  },
  {
    key: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    key: 'created_at',
    label: 'Created',
    formatFn: (value) => formatDate(value),
  },
]

export default function Overview({ volume, loading }) {
  const classes = useStyles({})
  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts)
  const hostsById = useSelector(resmgrHostsByIdSelector)

  const overviewData = useMemo(
    () =>
      volume
        ? [
            {
              key: 'Name',
              value: volume?.name,
            },
            {
              key: 'UUID',
              value: volume?.id,
            },
            {
              key: 'Host',
              value: volume?.hostId,
              render: ({ hostId }) =>
                hostsById?.[hostId]?.name || hostId ? (
                  <SimpleLink
                    src={routeManager.routes.openstack.hostDetails.path({
                      id: hostId,
                      tab: IHostDetailsPageTabs.Overview,
                    })}
                  >
                    {hostsById?.[hostId]?.name || hostId}
                  </SimpleLink>
                ) : null,
              condition: (value) => isAdmin(),
            },
            {
              key: 'Storage Backend',
              value: volume?.backend,
            },
            {
              key: 'Status',
              value: volume?.status,
              render: (vol) => <StatusCellComponent value={volume?.status} item={vol} />,
            },
            {
              key: 'Error Message',
              value: volume?.message?.user_message,
            },
            {
              key: 'Type',
              value: volume?.volume_type,
            },
            {
              key: 'Description',
              value: volume?.description,
            },
            {
              key: 'Attached VM',
              value: volume?.attachedVm,
              render: ({ attachedVm }) =>
                attachedVm ? (
                  <SimpleLink
                    src={routeManager.routes.openstack.vmDetails.path({
                      id: attachedVm?.id,
                      tab: IVmDetailsPageTabs.Overview,
                    })}
                  >
                    {attachedVm?.name}
                  </SimpleLink>
                ) : null,
            },
            {
              key: 'device',
              label: 'Device',
              value: volume?.device,
            },
            {
              key: 'Size',
              value: volume?.size,
              render: ({ size }) => humanReadableSize(size * 1024 * 1024 * 1024),
            },
            {
              key: 'Bootable',
              value: volume?.bootable,
            },
            {
              key: 'Multiattach',
              value: volume?.multiattach,
              render: ({ multiattach }) => (multiattach ? 'True' : 'False'),
            },
            {
              key: 'Source',
              value: <VolumeSourceCell item={volume} />,
              info: 'The resource used to create this volume. Blank if no resource used',
            },
            {
              key: 'Migration Status',
              value: volume?.migration_status,
            },
            {
              key: 'Created',
              value: volume?.created_at,
              render: ({ created_at }) => formatDate(created_at),
            },
            {
              key: 'Metadata',
              value: volume?.metadata,
              render: ({ metadata }) => {
                return !isNilOrEmpty(metadata) ? (
                  <Labels labels={metadata} variant="default" showMoreButton ellipsisAt={75} />
                ) : (
                  <Text variant="caption1">No Metadata</Text>
                )
              },
            },
            {
              key: 'Image Metadata',
              value: volume?.volume_image_metadata,
              render: ({ volume_image_metadata }) => {
                return !isNilOrEmpty(volume_image_metadata) ? (
                  <Labels
                    labels={volume_image_metadata}
                    variant="default"
                    showMoreButton
                    ellipsisAt={35}
                  />
                ) : (
                  <Text variant="caption1">No Metadata</Text>
                )
              },
            },
          ]
        : [],
    [hostsById, volume],
  )

  return (
    <Progress loading={loading}>
      <div className={classes.overview}>
        <div className={classes.column}>
          <OverviewCard item={volume} data={overviewData} icon="coins" />
        </div>
        <div className={classes.column}>
          <Card title="Volume Snapshots" withCustomBody>
            <Grid
              uniqueIdentifier="id"
              data={volume?.snapshots || []}
              columns={volumeSnapshotTableColumns}
              loading={loading}
              compact
              disableToolbar
            />
          </Card>
        </div>
      </div>
    </Progress>
  )
}
